/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useState } from 'react';

import { Grid, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useSkillEntity } from 'bundles/browse-common/contexts/SkillEntityContext';
import { useOnboardingV3Context } from 'bundles/onboarding-common/providers/OnboardingV3Provider';
import PathwaysCarouselCollections from 'bundles/page-config-common/components/shared/PathwaysCarouselCollections';
import type { Pathway } from 'bundles/page-config-common/components/shared/complex-spotlight-collection/toPathway';
import { toPathway } from 'bundles/page-config-common/components/shared/complex-spotlight-collection/toPathway';
import { useQuerySpotlightCollection } from 'bundles/page-config-common/components/shared/complex-spotlight-collection/useQuerySpotlightCollection';
import { usePageConfigComponentMetadata } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import { useS12nTargetDegrees } from 'bundles/page-config-common/contexts/S12nTargetDegreesContext';
import { useIsPathwaysForAllEnabled } from 'bundles/page-config-common/hooks/useIsPathwaysForAllEnabled';
import type { SpotlightCollectionWithSecondaryCta } from 'bundles/page-config-common/types/spotlight-collection';
import { formatRoleAndSkillExperimental } from 'bundles/page-config-common/utils/getEditCareerGoalCopy';
import { getRecommendedPathwayProps } from 'bundles/page-config-common/utils/getRecommendedPathwayProps';
import { useIntl } from 'bundles/page/lib/useIntl';
import { formatProductTypeWithCourseCount } from 'bundles/product-card/components/utils/productCardV2Utils';
import { ProductCardV2Placeholder } from 'bundles/search-common/components/search-cards/Placeholder';
import GeneralFilterableCollection from 'bundles/unified-common/components/GeneralFilterableCollection';
import RecommendedPathwayComponent from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayComponent';
import RecommendedRoleHeader from 'bundles/unified-common/components/recommended-pathway/RecommendedRoleHeader';
import RecommendedRoleSecondaryCta from 'bundles/unified-common/components/recommended-pathway/RecommendedRoleSecondaryCta';
import isRdpSlug from 'bundles/unified-common/utils/isRdpSlug';
import ShimmerOneCard from 'bundles/unified-home-common/components/loading-shimmer/ShimmerOneCard';

type Filter = {
  id: string;
  label: string;
  queryContext: {
    queryCollectionId: string;
    additionalInput: unknown;
  };
};

type Props = {
  showSingleCardDisplay: boolean;
  initialPathway: Pathway;
  filters: Filter[];
  recommendationCollection: SpotlightCollectionWithSecondaryCta;
  twoColumnLayout?: boolean;
  hideCoachButton?: boolean;
};

const ComplexSpotlightCollectionComponent = (props: Props) => {
  const { showSingleCardDisplay, recommendationCollection, filters, initialPathway, twoColumnLayout, hideCoachButton } =
    props;
  const enablePathwaysForAll = useIsPathwaysForAllEnabled();
  const intl = useIntl();
  const trackV3 = useTracker();

  const s12nTargetDegrees = useS12nTargetDegrees();

  const { getShouldShowOnboardingV3 } = useOnboardingV3Context();

  const query = useQuerySpotlightCollection();

  const maybeSkillEntity = useSkillEntity();

  const [activeFilterId, setActiveFilterId] = useState<string | undefined>(filters?.[0]?.id);

  const [pathwayMapping, setPathwayMapping] = useState<Record<string, Pathway>>(
    activeFilterId ? { [activeFilterId]: initialPathway } : {}
  );

  const skillRole = {
    slug: recommendationCollection.additionalInput.role_id ?? 'unknown-role',
    name: recommendationCollection.additionalInput.role_name ?? 'Unknown Role',
  };

  const hash = recommendationCollection.recommenderConfigurationHash;
  const context = recommendationCollection.context;
  const index = 0;
  const numDisplayedRows: number = showSingleCardDisplay ? 1 : 2;
  const onboardingEditLink = recommendationCollection.secondaryCta;

  const pathwaysHash = recommendationCollection.recommenderConfigurationHash;
  const pathwaysContext = recommendationCollection.context;

  const recommenderId = recommendationCollection.id || '';
  const roleUrl = isRdpSlug(skillRole.slug)
    ? `/career-academy/roles/${skillRole.slug}?recommenderId=${recommenderId}`
    : '';

  const role = {
    name: skillRole.name,
    slug: skillRole.slug,
    url: roleUrl,
  };

  const pageConfigComponentMetadata = usePageConfigComponentMetadata();
  const pageConfigComponentTrackingData = {
    id: pageConfigComponentMetadata?.id,
    index: pageConfigComponentMetadata?.index ?? -1,
    name: pageConfigComponentMetadata?.name ?? 'unknown',
    recommender: pageConfigComponentMetadata?.recommender,
  };

  const trackingData = {
    productCard: {
      recommender: { hash: pathwaysHash, context: pathwaysContext },
      pageConfigComponent: pageConfigComponentTrackingData,
    },
    carousel: {
      name: skillRole.slug.replace(/-/g, '_'),
      recommender: {
        hash: hash || 'hash',
        context,
      },
      id: recommenderId,
      section: index,
    },
    pageConfigComponent: pageConfigComponentTrackingData,
  };

  const handleFilterSelect = async (id?: string) => {
    const filter = filters.find((rec) => rec.id === id);

    if (filter) {
      if (!pathwayMapping[filter.id]) {
        try {
          const result = await query(filter.queryContext);

          if (result.data.Recommendations?.queryCollection) {
            const p1: Pathway = toPathway(result.data.Recommendations.queryCollection);

            setPathwayMapping((mapping) => ({
              ...mapping,
              [filter.id]: p1,
            }));
          }
        } finally {
          setActiveFilterId(filter.id);
        }
      } else {
        setActiveFilterId(filter.id);
      }
    }
  };

  const currentPathway = activeFilterId ? pathwayMapping?.[activeFilterId] : undefined;

  const { formattedRoleAndSkill, formattedRoleAndSkillLabel } = formatRoleAndSkillExperimental({
    recommenderId,
    roleName: skillRole.name,
    roleUrl,
    skillOrCollectionTitle: recommendationCollection.additionalInput.skill_name ?? maybeSkillEntity?.skill.skillName,
    enablePathwaysForAll,
    onRoleLinkClick: () => {
      trackV3('click_button', {
        button: {
          linkName: 'lihp_rdp_link',
          name: 'role_info',
          linkURL: role.url,
          pageConfigComponent: pageConfigComponentTrackingData,
        },
        carousel: trackingData.carousel,
      });
    },
  });

  const nameSize = 'subtitleMedium';

  const collectionTitle = (
    <RecommendedRoleHeader
      title={
        <Typography2 component="h2" variant="subtitleLarge" aria-label={formattedRoleAndSkillLabel}>
          {formattedRoleAndSkill}
        </Typography2>
      }
      onboardingText={onboardingEditLink?.ctaTitle}
      onboardingQuestion={onboardingEditLink?.ctaUrl.includes('skill-select') ? 'skill-select' : 'learning-goal'}
      trackingLinkName={onboardingEditLink?.ctaTitle ?? ''}
      hideHeaderLink={!enablePathwaysForAll}
      shouldShowOnboardingV3={getShouldShowOnboardingV3()}
    />
  );

  const content = enablePathwaysForAll ? (
    <PathwaysCarouselCollections
      filters={filters}
      onFilterSelect={handleFilterSelect}
      selectedFilterId={activeFilterId || ''}
      collections={currentPathway ? [{ id: activeFilterId || 'default', items: currentPathway.items }] : []}
      componentProps={(s12n, itemIndex) => {
        return getRecommendedPathwayProps(
          trackingData,
          s12n,
          itemIndex,
          currentPathway?.items ?? [],
          role,
          index,
          currentPathway ? s12nTargetDegrees?.[currentPathway.id] : undefined,
          nameSize,
          undefined,
          undefined,
          formatProductTypeWithCourseCount(intl, s12n.productVariant, s12n.courses.length),
          enablePathwaysForAll
        );
      }}
      title={collectionTitle}
      trackingData={trackingData}
      cardLoadingComponent={<ProductCardV2Placeholder />}
    />
  ) : (
    <Grid item xs={12}>
      <GeneralFilterableCollection
        filters={filters}
        onFilterSelect={handleFilterSelect}
        selectedFilterId={activeFilterId || ''}
        collections={currentPathway ? [{ id: activeFilterId || 'default', items: currentPathway.items }] : []}
        Component={RecommendedPathwayComponent}
        componentProps={(s12n, itemIndex) => {
          return getRecommendedPathwayProps(
            trackingData,
            s12n,
            itemIndex,
            currentPathway?.items ?? [],
            role,
            index,
            currentPathway ? s12nTargetDegrees?.[currentPathway.id] : undefined,
            nameSize,
            twoColumnLayout,
            { hideCoachButton }
          );
        }}
        title={collectionTitle}
        itemsGridConfig={{ xs: 12, sm: 12, md: 12, lg: 12 }}
        numDisplayedRows={numDisplayedRows}
        cardLoadingComponent={<ShimmerOneCard stage={3} />}
        secondaryCta={<RecommendedRoleSecondaryCta role={role} carouselTrackingData={trackingData.carousel} />}
        showMoreAmount={3}
        trackingData={trackingData}
      />
    </Grid>
  );

  return twoColumnLayout ? content : <PageGridContainer>{content}</PageGridContainer>;
};

export default ComplexSpotlightCollectionComponent;
